import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionForm, canPerformAction, type Action } from './forms';
import { findResource, type Resource } from './modules';
import { allRows, useAccess } from './api/data';
import { getObject, type GetPath, type Row, type Schema } from './api/client';
import { organizationAccessTarget } from './hr-permissions';
import { EntityDetail, useRelations } from './hr-pages';
import { Button, Empty, ErrorState, Loading, Status, useToast } from './ui';
import { InterviewFeedbackPage } from './interview-feedback';
import { ApplicationCreatePage } from './application-create';
import { EMAIL_ENABLED } from './features';
import { permittedWorkAreas } from './work-areas';

function useRouteResource() {
  const location = useRouterState({ select: (state) => state.location });
  const parts = location.pathname.split('/');
  const resource = findResource(parts[1] || '', parts[2] || '');
  const id = parts[3] || '';
  const operation = parts[4] || '';
  const search = location.search as {
    employeeId?: string;
    versionId?: string;
    taskId?: string;
    tab?: string;
    parentId?: string;
    childId?: string;
    childKey?: string;
  };
  return { resource, id, operation, search };
}
function useResourceRecord(resource: Resource | undefined, id: string, parentId?: string) {
  return useQuery({
    queryKey: ['detail', resource?.key, id, parentId],
    enabled: Boolean(resource && id),
    queryFn: async ({ signal }) => {
      if (resource!.detail)
        return getObject<Row>(resource!.detail, { [resource!.idParam || 'id']: id }, signal);
      if (resource!.parentScoped && !parentId && !resource!.detail)
        throw new Error('Откройте запись из родительской карточки');
      const rows = await allRows(
        resource!.path,
        resource!.parentScoped ? { id: parentId! } : {},
        signal,
      );
      const record = rows.find((row) => row.id === id);
      if (!record) throw new Error('Запись не найдена');
      return record;
    },
  });
}
export function actionOperation(action: Action) {
  if (action.operation) return action.operation;
  if (action.path.startsWith('/api/v1/process-tasks/')) return 'complete-task';
  if (action.method === 'PATCH')
    return action.path.includes('-versions/') ? 'edit-version' : 'edit';
  if (action.path.includes('-versions/') && action.path.endsWith('/publish'))
    return 'publish-version';
  if (action.path.includes('-versions/') && action.path.endsWith('/retire'))
    return 'retire-version';
  return action.path.split('/').at(-1) || 'edit';
}
function WorkflowSteps({
  resource,
  id,
  current,
}: {
  resource: Resource;
  id?: string;
  current: string;
}) {
  const navigate = useNavigate();
  if (resource.key !== 'job-postings') return null;
  const steps = [
    { label: 'Данные вакансии', operation: 'edit' },
    { label: 'Форма отклика', operation: 'application-form-versions' },
    { label: 'Воронка найма', operation: 'pipeline-versions' },
    { label: 'Публикация', operation: 'publish' },
  ];
  return (
    <div className="workflow-steps" role="tablist">
      {steps.map((step, index) => (
        <button
          type="button"
          role="tab"
          aria-selected={current === step.operation}
          key={step.operation}
          disabled={!id && index > 0}
          onClick={() => {
            if (id)
              void navigate({
                to: '/$section/$resource/$entityId/$operation',
                params: {
                  section: resource.section,
                  resource: resource.key,
                  entityId: id,
                  operation: step.operation,
                },
                search: { versionId: undefined },
              });
          }}
        >
          <span>{index + 1}</span>
          {step.label}
        </button>
      ))}
    </div>
  );
}
export function CreateResourcePage() {
  const { resource, search } = useRouteResource();
  const access = useAccess();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const organizations = useQuery({
    queryKey: ['creation-organizations'],
    enabled:
      [
        'organizations',
        'departments',
        'teams',
        'positions',
        'employees',
        'locations',
        'seniorities',
      ].includes(resource?.key || '') && Boolean(access.data),
    queryFn: ({ signal }) => allRows('/api/v1/organizations', {}, signal),
  });
  const canCreateAccount = ['accounts.manage', 'permissions.manage'].every((permission) =>
    access.can(permission, { entityType: 'Workspace', entityId: access.data?.account.workspaceId }),
  );
  const accountOptions = useQuery({
    queryKey: ['account-creation-options'],
    enabled: resource?.key === 'accounts' && canCreateAccount,
    queryFn: ({ signal }) =>
      getObject<Schema<'AccountOptionsResponse'>>('/api/v1/accounts/options', {}, signal),
  });
  if (!resource?.create) return <Empty title="Создание записи недоступно" />;
  if (access.isLoading) return <Loading />;
  if (!access.can(resource.createPermission || resource.managePermission || ''))
    return <Empty title="Недостаточно прав" />;
  if (
    ['departments', 'teams', 'positions', 'employees', 'locations', 'seniorities'].includes(
      resource.key,
    ) &&
    organizations.isPending
  )
    return <Loading />;
  if (
    ['departments', 'teams', 'positions', 'employees', 'locations', 'seniorities'].includes(
      resource.key,
    ) &&
    organizations.error
  )
    return <ErrorState error={organizations.error} />;
  const onlyOrganization = organizations.data?.filter((row) => row.status === 'ACTIVE');
  const organizationInitial =
    onlyOrganization?.length === 1 ? { organizationId: onlyOrganization[0]!.id } : {};
  const basicInitial = {
    ...organizationInitial,
    ...(resource.key === 'employees'
      ? {
          status: 'JOINING',
          employmentType: 'FULL_TIME',
          workFormat: 'REMOTE',
          additionalTeamIds: [],
        }
      : resource.key === 'teams'
        ? { status: 'ACTIVE' }
        : {}),
  };
  if (resource.key === 'accounts') {
    if (!canCreateAccount)
      return (
        <Empty
          title="Недостаточно прав"
          subtitle="Для создания учётной записи нужны права управления учётными записями и доступом ко всему рабочему пространству."
        />
      );
    if (accountOptions.isPending) return <Loading />;
    if (accountOptions.error) return <ErrorState error={accountOptions.error} />;
  }
  if (resource.key === 'organizations') {
    if (organizations.isPending) return <Loading />;
    if (organizations.error) return <ErrorState error={organizations.error} />;
    const existing = organizations.data?.find((row) => row.status === 'ACTIVE');
    if (existing)
      return (
        <Empty
          title="Организация уже создана"
          subtitle="В рабочем пространстве разрешена одна активная организация. Её название можно изменить."
        >
          <Button
            onClick={() =>
              void navigate({
                to: '/$section/$resource/$entityId/$operation',
                params: {
                  section: resource.section,
                  resource: resource.key,
                  entityId: existing.id,
                  operation: 'edit',
                },
              })
            }
          >
            Изменить организацию
          </Button>
        </Empty>
      );
  }
  if (resource.key === 'applications') return <ApplicationCreatePage />;
  const employeeId = search.employeeId;
  const action: Action = {
    label: `Создать: ${resource.singular.toLowerCase()}`,
    permission: resource.createPermission || resource.managePermission,
    path: resource.create,
    params: resource.employeeScoped && employeeId ? { id: employeeId } : undefined,
    initial:
      resource.key === 'accounts'
        ? { employeeId: employeeId || null, workAreas: ['HR'] }
        : employeeId
          ? { ...basicInitial, employeeId }
          : basicInitial,
    choices:
      resource.key === 'accounts'
        ? { '/api/v1/organizations': accountOptions.data?.organizations || [] }
        : undefined,
    hiddenFields:
      resource.key === 'accounts' && !permittedWorkAreas(access.data?.workAreas).includes('HR')
        ? ['employeeId']
        : onlyOrganization?.length === 1
          ? ['organizationId']
          : undefined,
    description:
      resource.key === 'accounts'
        ? EMAIL_ENABLED
          ? 'Выберите существующего сотрудника или создайте отдельную учётную запись. Приглашение придёт на указанную почту.'
          : 'Выберите существующего сотрудника или создайте отдельную учётную запись. После создания передайте пользователю ссылку активации.'
        : undefined,
  };
  return (
    <div className="resource-form-page">
      <ActionForm
        navigation={<WorkflowSteps resource={resource} current="edit" />}
        action={action}
        submitLabel={resource.key === 'job-postings' ? 'Далее' : 'Создать'}
        onClose={() =>
          void navigate({
            to: '/$section/$resource',
            params: { section: resource.section, resource: resource.key },
          })
        }
        onSuccess={(record) => {
          toast('Сохранено');
          void client.invalidateQueries();
          if (resource.key === 'employees')
            void navigate({
              to: '/employees/profile/$employeeId',
              params: { employeeId: record.id },
            });
          else if (resource.key === 'job-postings')
            void navigate({
              to: '/$section/$resource/$entityId/$operation',
              params: {
                section: resource.section,
                resource: resource.key,
                entityId: record.id,
                operation: 'application-form-versions',
              },
              search: { versionId: undefined },
            });
          else
            void navigate({
              to: '/$section/$resource/$entityId',
              params: { section: resource.section, resource: resource.key, entityId: record.id },
            });
        }}
      />
    </div>
  );
}
export function ResourceDetailPage() {
  const { resource, id, search } = useRouteResource();
  const record = useResourceRecord(resource, id, search.parentId);
  const related = useRelations(resource?.fields || []);
  const navigate = useNavigate();
  if (!resource) return <Empty title="Страница не найдена" />;
  if (record.isLoading) return <Loading />;
  if (record.error || !record.data) return <ErrorState error={record.error} />;
  return (
    <EntityDetail
      tab={search.tab}
      onTab={(tab) =>
        void navigate({
          to: '/$section/$resource/$entityId',
          params: { section: resource.section, resource: resource.key, entityId: id },
          search: { tab, parentId: search.parentId },
        })
      }
      resource={resource}
      record={record.data}
      related={related}
      onClose={() =>
        void navigate({
          to: '/$section/$resource',
          params: { section: resource.section, resource: resource.key },
        })
      }
      onRelated={(next, row) =>
        void navigate({
          to: '/$section/$resource/$entityId',
          params: { section: next.section, resource: next.key, entityId: row.id },
          search: { parentId: next.parentScoped ? id : undefined },
        })
      }
      onAction={(action) =>
        void navigate({
          to: '/$section/$resource/$entityId/$operation',
          params: {
            section: resource.section,
            resource: resource.key,
            entityId: id,
            operation: actionOperation(action),
          },
          search: {
            parentId: search.parentId,
            versionId:
              actionOperation(action).includes('version') && action.params?.id !== id
                ? action.params?.id
                : undefined,
            taskId: actionOperation(action) === 'complete-task' ? action.params?.id : undefined,
          },
        })
      }
      onChildAction={(action, childKey, childId) =>
        void navigate({
          to: '/$section/$resource/$entityId/$operation',
          params: {
            section: resource.section,
            resource: resource.key,
            entityId: id,
            operation: actionOperation(action),
          },
          search: { childKey, childId, parentId: search.parentId },
        })
      }
    />
  );
}
export function ResourceActionPage() {
  const { resource, id, operation, search } = useRouteResource();
  const record = useResourceRecord(resource, id, search.parentId);
  const access = useAccess();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const childDefinition = (resource?.children?.(record.data || { id }) || []).find(
    (child) => child.key === search.childKey,
  );
  const prefillPath =
    resource?.key === 'specialisations' && operation === 'allowed-seniorities'
      ? '/api/v1/specialisations/{id}/allowed-seniorities'
      : resource?.key === 'job-postings' &&
          ['application-form-versions', 'pipeline-versions'].includes(operation)
        ? `/api/v1/job-postings/{id}/${operation}`
        : resource?.key === 'offers' && operation === 'versions'
          ? '/api/v1/offers/{id}/versions'
          : null;
  const prefill = useQuery<Record<string, unknown>>({
    queryKey: ['action-prefill', resource?.key, id, operation],
    enabled: Boolean(prefillPath),
    queryFn: async ({ signal }) => {
      if (resource?.key === 'specialisations')
        return getObject<Row>(prefillPath as GetPath, { id }, signal);
      const rows = await allRows(prefillPath as GetPath, { id }, signal);
      return rows.sort((a, b) => Number(b.versionNumber) - Number(a.versionNumber))[0] || {};
    },
  });
  const childRecord = useQuery({
    queryKey: ['action-child', resource?.key, id, search.childKey, search.childId],
    enabled: Boolean(record.data && childDefinition && search.childId),
    queryFn: async ({ signal }) => {
      const values = childDefinition!.collection
        ? (
            await getObject<Record<string, Row[]>>(
              childDefinition!.path,
              childDefinition!.params || {},
              signal,
            )
          )[childDefinition!.collection] || []
        : await allRows(childDefinition!.path, childDefinition!.params || {}, signal);
      const child = values.find((row) => row.id === search.childId);
      if (!child) throw new Error('Запись не найдена');
      return child;
    },
  });
  const versions = useQuery({
    queryKey: ['action-version', resource?.key, id, search.versionId],
    enabled: Boolean(resource && search.versionId),
    queryFn: async ({ signal }) => {
      const rows = await allRows(
        `/api/v1/${resource!.key === 'templates' ? 'process-templates' : resource!.key}/{id}/versions` as GetPath,
        { id },
        signal,
      );
      const version = rows.find((row) => row.id === search.versionId);
      if (!version) throw new Error('Версия не найдена');
      return version;
    },
  });
  const tasks = useQuery({
    queryKey: ['action-task', resource?.key, id, search.taskId],
    enabled: Boolean(resource && search.taskId),
    queryFn: async ({ signal }) => {
      const rows = await allRows(
        `/api/v1/${resource!.key === 'onboarding' ? 'onboarding-processes' : 'offboarding-processes'}/{id}/tasks` as GetPath,
        { id },
        signal,
      );
      const task = rows.find((row) => row.id === search.taskId);
      if (!task) throw new Error('Задача не найдена');
      return task;
    },
  });
  if (!resource) return <Empty title="Страница не найдена" />;
  if (
    record.isLoading ||
    versions.isLoading ||
    tasks.isLoading ||
    childRecord.isLoading ||
    prefill.isLoading ||
    access.isLoading
  )
    return <Loading />;
  if (record.error || !record.data) return <ErrorState error={record.error} />;
  if (versions.error) return <ErrorState error={versions.error} />;
  if (childRecord.error) return <ErrorState error={childRecord.error} />;
  if (prefill.error) return <ErrorState error={prefill.error} />;
  const row = record.data;
  if (tasks.error) return <ErrorState error={tasks.error} />;
  const back = () =>
    void navigate({
      to: '/$section/$resource/$entityId',
      params: { section: resource.section, resource: resource.key, entityId: id },
      search: { parentId: search.parentId },
    });
  if (resource.key === 'interviews' && operation === 'feedback')
    return <InterviewFeedbackPage id={id} onClose={back} />;
  let action: Action | undefined =
    operation === 'edit' &&
    resource.edit &&
    (resource.canEdit ? resource.canEdit(row) : !resource.hidden || row.status === 'DRAFT')
      ? {
          label: `Изменить: ${resource.singular.toLowerCase()}`,
          method: 'PATCH',
          path: resource.edit,
          params: { [resource.idParam || 'id']: id },
          initial: row,
          permission: resource.editPermission || resource.managePermission,
        }
      : resource.actions?.(row).find((item) => actionOperation(item) === operation);
  if (childRecord.data && childDefinition?.actions)
    action = childDefinition
      .actions(childRecord.data, row)
      .find((item) => actionOperation(item) === operation);
  if (search.taskId && tasks.data?.canComplete === true)
    action = {
      label: 'Завершить задачу',
      path: '/api/v1/process-tasks/{id}/complete',
      params: { id: search.taskId },
      initial: { version: tasks.data.version },
      permission: resource.editPermission || resource.managePermission,
    };
  if (action && !search.childId && !search.taskId && !search.versionId)
    action = { ...action, initial: { ...row, ...action.initial } };
  if (search.versionId && versions.data) {
    const prefix =
      resource.key === 'templates'
        ? 'process-template-versions'
        : resource.key === 'skills'
          ? 'skill-versions'
          : resource.key === 'role-profiles'
            ? 'role-profile-versions'
            : 'employee-evaluation-template-versions';
    action =
      versions.data.status === 'DRAFT' &&
      (resource.key === 'templates' ||
        (versions.data.allowedActions as string[] | undefined)?.includes(
          operation === 'edit-version' ? 'edit' : 'publish',
        ))
        ? {
            label: operation === 'edit-version' ? 'Редактировать версию' : 'Опубликовать версию',
            method: operation === 'edit-version' ? 'PATCH' : 'POST',
            path: `/api/v1/${prefix}/{id}${operation === 'edit-version' ? '' : '/publish'}` as Action['path'],
            params: { id: search.versionId },
            initial: versions.data,
            permission: resource.editPermission || resource.managePermission,
          }
        : undefined;
  }
  if (action && prefill.data)
    action = {
      ...action,
      initial: {
        ...action.initial,
        ...prefill.data,
        version: resource.key === 'specialisations' ? prefill.data.version : row.version,
      },
    };
  if (action && resource.key === 'offers' && operation === 'send')
    action = {
      ...action,
      hiddenFields: ['emailTemplateVersionId'],
      description: 'Кандидату придёт ссылка на текущую версию оффера.',
    };
  if (action && resource.key === 'job-postings' && ['publish', 'unpublish'].includes(operation))
    action = {
      ...action,
      schemaOverrides: {
        channels: {
          type: 'array',
          minItems: 1,
          items: {
            type: 'string',
            enum: row[
              operation === 'publish' ? 'publishableChannels' : 'unpublishableChannels'
            ] as string[],
          },
        },
      },
    };
  if (action && resource.key === 'reviews' && operation === 'reviewer') {
    const manager = (row.submissions as Row[] | undefined)?.find(
      (item) => item.reviewerType === 'LINE_MANAGER',
    );
    action = {
      ...action,
      choices: { '/api/v1/accounts': (row.reviewerOptions as Row[] | undefined) || [] },
      initial: {
        ...action.initial,
        submissionVersion: manager?.version,
        reviewerAccountId: manager?.reviewerAccountId,
      },
    };
  }
  if (
    resource.key === 'job-postings' &&
    operation === 'publish' &&
    row.approvalStatus !== 'APPROVED'
  )
    return (
      <div className="form-page">
        <h1>Публикация вакансии</h1>
        <WorkflowSteps resource={resource} id={id} current={operation} />
        <section className="panel approval-step">
          <h2>Согласование</h2>
          <Status value={String(row.approvalStatus)} />
          <p className="muted">Публикация доступна после согласования вакансии.</p>
          <div className="entity-actions">
            {resource
              .actions?.(row)
              .filter(
                (item) =>
                  ['submit-approval', 'approve', 'reject'].includes(actionOperation(item)) &&
                  canPerformAction(item, access.can),
              )
              .map((item) => (
                <Button
                  key={item.label}
                  onClick={() =>
                    void navigate({
                      to: '/$section/$resource/$entityId/$operation',
                      params: {
                        section: resource.section,
                        resource: resource.key,
                        entityId: id,
                        operation: actionOperation(item),
                      },
                      search: { versionId: undefined },
                    })
                  }
                >
                  {item.label}
                </Button>
              ))}
          </div>
        </section>
        <Button onClick={back}>К вакансии</Button>
      </div>
    );
  if (
    !action ||
    !canPerformAction(action, (permission) =>
      access.can(permission, organizationAccessTarget(resource.key, row)),
    )
  )
    return (
      <Empty title="Действие недоступно" subtitle="Проверьте статус записи и права доступа." />
    );
  if (
    resource.key === 'reviews' &&
    operation === 'reviewer' &&
    !(row.reviewerOptions as Row[] | undefined)?.length
  )
    return (
      <Empty
        title="Нет подходящих оценщиков"
        subtitle="Нужна активная учётная запись другого сотрудника той же организации."
      >
        <Button onClick={back}>К оценке</Button>
      </Empty>
    );
  const currentAction = action;
  return (
    <div className="resource-form-page">
      <ActionForm
        navigation={<WorkflowSteps resource={resource} id={id} current={operation} />}
        key={`${operation}:${search.versionId || id}`}
        action={currentAction}
        submitLabel={
          resource.key === 'job-postings' &&
          ['edit', 'application-form-versions', 'pipeline-versions'].includes(operation)
            ? 'Далее'
            : undefined
        }
        onClose={back}
        onSuccess={(result) => {
          toast('Сохранено');
          void client.invalidateQueries();
          if (operation === 'delete') {
            void navigate({
              to: '/$section/$resource',
              params: { section: resource.section, resource: resource.key },
            });
            return;
          }
          if (
            result.type &&
            ['EXPORT', 'CANDIDATE_IMPORT', 'RETENTION_DRY_RUN'].includes(String(result.type))
          ) {
            void navigate({
              to: '/operations/result/$operationId',
              params: { operationId: result.id },
            });
            return;
          }
          if (
            (resource.key === 'work-plans' && operation === 'reviews') ||
            (resource.key === 'reviews' && operation === 'corrections')
          ) {
            void navigate({
              to: '/$section/$resource/$entityId',
              params: { section: 'performance', resource: 'reviews', entityId: result.id },
            });
            return;
          }
          if (resource.key === 'applications' && ['interviews', 'offers'].includes(operation)) {
            void navigate({
              to: '/$section/$resource/$entityId',
              params: { section: 'recruitment', resource: operation, entityId: result.id },
            });
            return;
          }
          const next =
            resource.key === 'job-postings'
              ? operation === 'edit'
                ? 'application-form-versions'
                : operation === 'application-form-versions'
                  ? 'pipeline-versions'
                  : operation === 'pipeline-versions'
                    ? 'publish'
                    : null
              : null;
          if (next)
            void navigate({
              to: '/$section/$resource/$entityId/$operation',
              params: {
                section: resource.section,
                resource: resource.key,
                entityId: id,
                operation: next,
              },
              search: { versionId: undefined },
            });
          else back();
        }}
      />
    </div>
  );
}
